import { HttpInterceptor, HttpHandler, HttpRequest, HttpEvent, HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

import { Observable, catchError, retry, tap, throwError, timer } from "rxjs";
import { Capacitor } from "@capacitor/core";

import { environment } from "src/environments/environment";
import buildInfoJson from "src/environments/build-info.json";
import { HelperNavControllerService } from "../helpers/helper-nav-controller.service";
import { HelperSessionControllerService } from "../helpers/helper-session-controller.service";
import { HelperDebugLoggerControllerService } from "../helpers/helper-debug-logger-controller.service";
import { HelperBuildUpdateControllerService } from "../helpers/helper-build-update-controller.service";
import { ROUTE_DIRECTION } from "../types/route.type";
import { getEnvironmentAuthRoute } from "../helpers/helper-route.util";

@Injectable()
export class SessionInterceptor implements HttpInterceptor {
  private debugLogger = inject(HelperDebugLoggerControllerService);
  private helperSessionControllerService = inject(HelperSessionControllerService);
  private helperNavControllerService = inject(HelperNavControllerService);
  private buildUpdateService = inject(HelperBuildUpdateControllerService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const headers: Record<string, string> = {
      'x-app-version': buildInfoJson.version || '1.0.0',
      'x-app-platform': Capacitor.getPlatform(),
      'x-app-persona': environment.app || 'merchant',
    };

    const session = this.helperSessionControllerService?.session;
    if (session) {
      if (session['x-tenant-id']) {
        headers['x-tenant-id'] = String(session['x-tenant-id']);
      }
      if (session.token) {
        headers['Authorization'] = `Bearer ${session.token}`;
      }
    }
    request = request.clone({ setHeaders: headers });

    const isIdempotentMethod = request.method === 'GET' || request.method === 'HEAD';

    const handle$ = isIdempotentMethod
      ? next.handle(request).pipe(
          retry({
            count: 2,
            delay: (error: unknown, retryCount: number) => {
              if (error instanceof HttpErrorResponse && ((error.status >= 400 && error.status < 500) || error.status === 503)) {
                return throwError(() => error);
              }
              return timer(retryCount * 500);
            }
          })
        )
      : next.handle(request);

    return handle$.pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          const isOtpUrl = request.url.includes('/otp');
          this.debugLogger.addLog(
            isOtpUrl ? 'otp' : 'response',
            `${request.method} ${request.url.replace(/.*\/api/, '/api')}`,
            event.body,
            event.status
          );
        }
      }),
      catchError((error: HttpErrorResponse) => {
        this.debugLogger.addLog(
          'error',
          `HTTP ${error.status}: ${request.method} ${request.url.replace(/.*\/api/, '/api')}`,
          error.error || error.message,
          error.status
        );

        if (error.status === 401) {
          window.localStorage.removeItem('session');
          this.helperNavControllerService.navigate(getEnvironmentAuthRoute(), ROUTE_DIRECTION.Root);
        } else if (error.status === 426) {
          this.buildUpdateService.handle426Upgrade(error.error?.data || error.error);
        } else if (
          error.status === 503 &&
          (error.error?.message === 'server--maintenance--active' || error.error?.data?.isMaintenance)
        ) {
          this.buildUpdateService.handle503Maintenance(error.error?.data || error.error);
        }
        return throwError(() => error);
      })
    );
  }
}

